import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

/**
 * Hook to identify which admin tier the current user belongs to within
 * the active tenant. Naming aligned with hierarchy verified against route
 * guards in MainApp.tsx (admin > company_admin in permissions):
 *
 *   Tenant Super Admin  – row in `tenant_admins` with role='super_admin' and status='active'.
 *                         Top of the hierarchy. Manages tenant + admins + tenant settings.
 *   Tenant Admin        – `user_roles.role = 'admin'` (no tenant_admins entry).
 *                         Broad access across the whole tenant — sees all branches.
 *   Branch Admin        – `user_roles.role = 'company_admin'`.
 *                         Scoped to one branch (sub-company). Sees only their own branch's users.
 *   User                – `user_roles.role = 'user'`.
 *
 * `isBranchAdmin` here is now strictly company_admin role. The previous version
 * treated plain admin as "branch admin", which conflicted with the team's
 * mental model and caused Tenant Admins to be incorrectly company-filtered
 * in the User Management view.
 */
export function useTenantAdmin() {
  const { user } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const currentTenantId = tenant?.tenant_id;

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-admin-status', user?.id, currentTenantId],
    queryFn: async () => {
      if (!user?.supabaseAuthId || !currentTenantId) {
        return { isTenantSuperAdmin: false, tenantAdminRole: null };
      }

      // Check if user is in tenant_admins for this tenant
      const { data: tenantAdmin, error } = await supabase
        .from('tenant_admins')
        .select('id, role, status')
        .eq('user_id', user.supabaseAuthId)
        .eq('tenant_id', currentTenantId)
        .maybeSingle();

      if (error) {
        console.error('Error checking tenant admin status:', error);
        return { isTenantSuperAdmin: false, tenantAdminRole: null };
      }

      const isTenantSuperAdmin = tenantAdmin?.role === 'super_admin' && tenantAdmin?.status === 'active';

      return {
        isTenantSuperAdmin,
        tenantAdminRole: tenantAdmin?.role || null,
      };
    },
    enabled: !!user?.supabaseAuthId && !!currentTenantId && !isMasterTenant,
  });

  // Master-tenant admins are always at the top of the hierarchy.
  if (isMasterTenant) {
    return {
      isTenantSuperAdmin: true,
      isTenantAdmin: false,
      isBranchAdmin: false,
      tenantAdminRole: 'super_admin',
      isLoading: false,
    };
  }

  const isTenantSuperAdmin = data?.isTenantSuperAdmin || false;
  const hasAdminRole = user?.roles?.includes('admin') || false;
  const hasCompanyAdminRole = user?.roles?.includes('company_admin') || false;

  // Tenant Admin = has plain admin role but is NOT a tenant super admin.
  //   Sees all users in the tenant. Not company-filtered.
  const isTenantAdmin = hasAdminRole && !isTenantSuperAdmin;

  // Branch Admin = has company_admin role only.
  //   Sees only their own branch's users.
  const isBranchAdmin = hasCompanyAdminRole && !hasAdminRole && !isTenantSuperAdmin;

  return {
    isTenantSuperAdmin,
    isTenantAdmin,
    isBranchAdmin,
    tenantAdminRole: data?.tenantAdminRole || null,
    isLoading,
  };
}
